import {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  Fragment,
  createContext,
  useContext,
  StrictMode
} from "react";
import MultiRef from "react-multi-ref";
import shortid from "shortid";
import move from "lodash-move";
import {
  DndContext,
  useDroppable,
  useDraggable,
  MouseSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  restrictToHorizontalAxis,
  restrictToFirstScrollableAncestor
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import VisibilitySensor from "./viewability";
import { enrichData, adjustMenu } from "./utils";
import { calculateTabsOffsets } from "./offsets";

import {
  Tabs,
  InputRadio,
  Glider,
  Draggable,
  Container,
  Header,
  LeftBox,
  Spacer,
  HiddenButton,
  HiddenItemIcon,
  ArrowUp,
  HiddenDropdown,
  HiddenItem,
  HiddenItemShortcut,
  Extra,
  AddTab,
  Pane,
  Tab,
  TabLabel,
  TabExtra,
  TabIcon,
  TabTitle,
  TabDeleteIcon,
  TabEdit,
  AddPan,
  Button,
  Input,
  TabMenuBox,
  ChevronUp,
  ChevronDown,
  Portal
} from "./components";

const TabLabelContext = createContext();
const TabBarContext = createContext();

const PillsContainer = (props) => {
  const {
    tabs,
    extra,
    className,
    tabsClassName,
    defaultActiveTab = 1,
    defaultActiveTabId,
    spacing = 5,
    enableReinitialize = false,
    showHiddenItemsMenu = true,
    destroyInactiveTabPane = false,
    allowEmpty = false,
    setActiveTabOnClick = true,
    originData,
    onChange,
    addTab,
    moreIcon
  } = props;

  const idRef = useRef(shortid.generate());
  const id = idRef.current; //props.id ?? idRef.current;
  const tabBarRef = useRef();
  const tabBarInnerRef = useRef();
  const currentTabRef = useRef();
  const previousRef = useRef();
  const [tabBarIsVisible, setTabBarIsVisible] = useState(false);
  const [onEdit, setOnEdit] = useState(false);
  const [data, setData] = useState(enrichData(tabs, previousRef.current));
  const [activeTab, setActiveTabState] = useState(() => {
    if (currentTabRef.current) {
      return currentTabRef.current;
    }

    const id = defaultActiveTabId
      ? defaultActiveTabId
      : data[defaultActiveTab - 1]?.id;

    currentTabRef.current = id;

    return id;
  });

  const [tabRefs] = useState(() => new MultiRef());
  const activeTabRef = tabRefs.map.get(activeTab);
  const activeTabOffset = activeTabRef?.offsetWidth;
  const [hiddenItems, setHiddenItems] = useState(new Set());
  const [showHiddenItems, setShowHiddenItems] = useState(false);
  const initialized = useRef(0);
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      distance: 20 // Require the mouse to move by 20 pixels before activating
    }
  });
  const sensors = useSensors(mouseSensor);

  useEffect(() => {
    // it must reinitialize at least 2 to let the refs be set
    if (enableReinitialize || initialized.current === 0) {
      //console.log("#REINITIALIZE TABS", tabs);
      const data = enrichData(tabs, previousRef.current);
      setData(data);
      previousRef.current = data.map(({ tab, elm, id }) => {
        return { tab, elm, id };
      });
    }
    initialized.current++;
  }, [enableReinitialize, tabs]);

  useEffect(() => {
    // console.log("#EFFECT DEFAULTACTIVETABID", defaultActiveTabId);
    if (defaultActiveTabId && defaultActiveTabId !== currentTabRef.current) {
      currentTabRef.current = defaultActiveTabId;
      setActiveTabState(defaultActiveTabId);
    }
  }, [defaultActiveTabId]);

  useEffect(() => {
    if (!tabBarIsVisible) return;
    //console.log("#EFFECT SCROLLINTOVIEW");
    activeTabRef?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center"
    });
  }, [activeTabRef, tabBarIsVisible]);

  const setActiveTab = useCallback((id) => {
    setActiveTabState(id);
    currentTabRef.current = id;
  }, []);

  const handleChangeTab = useCallback(
    (id, index) => {
      if (setActiveTabOnClick) setActiveTab(id);
      onChange?.(index, id);
    },
    [onChange, setActiveTabOnClick, setActiveTab]
  );

  const addNewTab = (e) => {
    const newData = structuredClone(originData);
    const newTab = addTab.getNewData();

    newData.push(newTab);
    addTab.onAdd?.(newData);
  };

  const handleDragTab = useCallback(
    (from, to, onDragTab) => {
      setData(move([...data], from, to));

      if (onDragTab) {
        const newData = originData ? move([...originData], from, to) : null;
        if (newData) onDragTab(newData, from, to);
        else onDragTab(from, to);
      }
    },
    [data, originData]
  );

  // The active tab is gone (e.g. the variant open was discarded): the first
  // tab is shown active, and the change is asked after the render (an
  // effect: onChange sets the state of the parent, and may navigate). No
  // tab at all: nothing to ask (the parent shows something else, e.g. it
  // leaves the page).
  const activeTabMissing =
    Boolean(activeTab) && !data.some((tab) => tab.id === activeTab);
  const shownTab = activeTabMissing ? data[0]?.id : activeTab;
  // Asked once per missing tab: a parent that does not follow (it asks the
  // user first) is not asked again at every render
  const fallbackAskedRef = useRef(null);

  useEffect(() => {
    if (!activeTabMissing) {
      fallbackAskedRef.current = null;
      return;
    }
    if (data.length === 0 || fallbackAskedRef.current === activeTab) return;

    fallbackAskedRef.current = activeTab;
    handleChangeTab(data[0].id, 0);
  }, [activeTab, activeTabMissing, data, handleChangeTab]);

  // Drop the tabs that don't exist anymore from the hidden items: the
  // visibility sensors of the existing tabs keep the set up to date. (It was
  // reset at every tab bar render, which forced a second render and lost the
  // tabs still hidden after a tab switch.)
  useEffect(() => {
    setHiddenItems((state) => {
      const ids = new Set(data.map((tab) => tab.id));
      const kept = [...state].filter((id) => ids.has(id));
      return kept.length === state.size ? state : new Set(kept);
    });
  }, [data]);

  const tabBar = useMemo(() => {
    //console.log("#RENDER TABS");
    const tabAttivo = shownTab;
    const offsets = calculateTabsOffsets(tabRefs, spacing, data.length);
    let isDraggable = false;

    const tabs = (
      <Tabs $spacing={spacing} id="tab-bar-inner" ref={tabBarInnerRef}>
        {data.map((tab, index) => {
          const isActive = tab.id === tabAttivo;
          const inputId = `${id}-${tab.id}`;
          if (isActive && tab.tab?.draggable) isDraggable = true;

          return (
            <Fragment key={`frag-${id}-${tab.id}-${index}-${tabBarIsVisible}`}>
              <InputRadio
                $index={index}
                $translate={offsets[index]}
                $id={inputId}
                $group={id}
                $active={isActive}
                onClick={() => {
                  !onEdit && handleChangeTab(tab.id, index);
                }}
                onChange={() => true}
                key={`in-input-${id}-${tab.id}`}
                value={isActive ? "true" : "false"}
              />
              <SimpleTab
                key={`simple-${id}-${tab.id}-${index}`}
                id={id}
                inputId={inputId}
                index={index}
                isActive={isActive}
                tab={tab}
              />
            </Fragment>
          );
        })}
        {!onEdit && (
          <Glider
            key={`${id}-glider`}
            className="glider"
            $tabWidth={activeTabOffset}
          >
            {isDraggable && <Draggable />}
          </Glider>
        )}
      </Tabs>
    );

    return tabs;
  }, [
    shownTab,
    spacing,
    data,
    onEdit,
    id,
    activeTabOffset,
    tabBarIsVisible,
    tabRefs,
    handleChangeTab
  ]);

  const handleVisibilityChange = useCallback(
    (visible) => {
      if (!visible) hiddenItems.clear();
      setTabBarIsVisible(visible);
    },
    [hiddenItems]
  );

  if (addTab?.enabled && !addTab.getNewData) {
    throw new Error("getNewData is required when addTab is enabled");
  }
  if (addTab?.enabled && !originData) {
    throw new Error("originData is required when addTab is enabled");
  }

  if (!activeTab) return null;

  // The pane shown: the tab Pills switches to by itself, or the one the
  // parent chose (setActiveTabOnClick false: it follows onChange or not)
  const paneTab = setActiveTabOnClick ? shownTab : activeTab;

  return (
    <StrictMode>
      <div id="tabbar" ref={tabBarRef}>
        <VisibilitySensor
          key={`vis-${id}-tabbar-sensor`}
          ref={tabBarRef.current}
          onChange={handleVisibilityChange}
        >
          <Container key={id} className={className}>
            <Header className={tabsClassName}>
              <DndContext
                modifiers={[
                  restrictToHorizontalAxis,
                  restrictToFirstScrollableAncestor
                ]}
                autoScroll={true}
                sensors={sensors}
                onDragEnd={({ active, over }) =>
                  handleDragTab(active.id, over.id, data[active.id].tab?.onDrag)
                }
              >
                <TabBarContext.Provider
                  value={{
                    originData,
                    activeTab,
                    tabRefs,
                    tabBarInnerRef,
                    allowEmpty,
                    setHiddenItems,
                    notifyEdit: setOnEdit,
                    globalEdit: onEdit
                  }}
                >
                  {tabBar}
                </TabBarContext.Provider>
              </DndContext>
              <Spacer />
              <LeftBox id="tabbar-left-box">
                {showHiddenItemsMenu && hiddenItems.size > 0 && (
                  <HiddenButton
                    onClick={() => setShowHiddenItems((state) => !state)}
                  >
                    {moreIcon ?? <HiddenItemIcon />}
                    {showHiddenItems && hiddenItems.size > 0 && (
                      <>
                        <ArrowUp />
                        <HiddenDropdown $left={-210}>
                          {Array.from(hiddenItems).map((id, index) => (
                            <HiddenItem
                              key={`hidden-${id}-${index}`}
                              onClick={() => {
                                handleChangeTab(id, index);
                              }}
                            >
                              <TabLabelContext.Provider
                                value={{
                                  isActive: false,
                                  index
                                }}
                              >
                                {data.find((i) => i.id === id)?.elm}
                              </TabLabelContext.Provider>
                            </HiddenItem>
                          ))}
                        </HiddenDropdown>
                      </>
                    )}
                  </HiddenButton>
                )}
                {extra && (
                  <>
                    <Extra>{extra}</Extra>
                  </>
                )}
                {addTab?.enabled && (
                  <AddTab onClick={addNewTab}>
                    {addTab.icon ? addTab.icon : <AddPan />}
                  </AddTab>
                )}
              </LeftBox>
            </Header>

            {destroyInactiveTabPane ? (
              <Pane key={`pane-${id}`} id={id}>
                {data.find((i) => i.id === paneTab)?.pane}
              </Pane>
            ) : (
              data.map(({ id, pane }) => {
                return (
                  <Pane key={`pane-${id}`} id={id} hidden={id !== paneTab}>
                    {pane}
                  </Pane>
                );
              })
            )}
          </Container>
        </VisibilitySensor>
      </div>
    </StrictMode>
  );
};

function SimpleTab({ id, inputId, index, tab, isActive }) {
  const {
    originData,
    tabRefs,
    tabBarInnerRef,
    setHiddenItems,
    notifyEdit,
    globalEdit,
    allowEmpty
  } = useContext(TabBarContext);
  const [isVisible, setIsVisible] = useState(true);
  const disabled = globalEdit || tab.tab?.draggable !== true;
  const { isOver, setNodeRef: setDropRef } = useDroppable({
    id: index,
    disabled
  });
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    transform,
    isDragging
  } = useDraggable({ id: index, disabled });

  const style = {
    opacity: isDragging ? 0.8 : 1,
    //zIndex: 1,
    zIndex: isOver ? 0 : 1,
    transform: CSS.Translate.toString(transform),
    borderRadius: "8px",
    border: isDragging
      ? "1px solid greenyellow"
      : isOver
      ? "1px solid #888"
      : "",
    background:
      isOver &&
      "repeating-linear-gradient( -55deg,#111,#111 5px,#222 5px,#222 10px)"
  };

  const handleVisibilityChange = useCallback(
    (isVisible) => {
      setIsVisible(isVisible);
      setHiddenItems((state) => {
        if (!isVisible && state.has(tab.id)) return state;

        if (isVisible) state.delete(tab.id);
        else state.add(tab.id);

        return new Set(state);
      });
    },
    [setHiddenItems, tab.id]
  );

  return (
    <>
      <VisibilitySensor
        key={`vis-${id}-${tab.id}-${index}`}
        ref={tabRefs.map.get(tab.id)}
        onChange={handleVisibilityChange}
      >
        <div
          style={style}
          {...listeners}
          {...attributes}
          key={`dnd-${id}-${tab.id}-${index}`}
          ref={(node) => {
            setDragRef(node);
            setDropRef(node);
            tabRefs.ref(tab.id)(node);
          }}
        >
          <Tab
            $index={index}
            $id={inputId}
            $tabWidth={tabRefs.map.get(tab.id)?.offsetWidth ?? 20}
            $isActive={isActive}
            className={isActive ? "active" : ""}
            key={`tab-${id}-${tab.id}-${index}`}
          >
            {
              <TabLabelContext.Provider
                value={{
                  isActive: globalEdit ? false : isActive,
                  isVisible: isVisible,
                  tabBarInnerRef: tabBarInnerRef,
                  index,
                  notifyEdit,
                  globalEdit,
                  originData,
                  allowEmpty
                }}
              >
                {tab.elm}
              </TabLabelContext.Provider>
            }
          </Tab>
        </div>
      </VisibilitySensor>
    </>
  );
}

export function TabLabelComponent({
  title,
  titleColor = "#888",
  icon,
  iconColor = "#888",
  width = "200px",
  extra,
  editable,
  onEdit,
  editFormatter,
  deletable,
  onDelete,
  onConfirmDelete,
  activeColor = "#fff",
  menu
}) {
  const {
    isActive,
    isVisible,
    tabBarInnerRef,
    index,
    notifyEdit,
    globalEdit,
    originData,
    allowEmpty
  } = useContext(TabLabelContext);

  const menuBoxRef = useRef(null);
  const [edit, setEditStatus] = useState(false);
  const [newTitle, setNewTitle] = useState(title);
  const [showMenu, setShowMenu] = useState(false);
  const tabMenuBoxRef = useRef(null);

  if (!title) {
    throw new Error("Tab title is required");
  }
  if (editable && !onEdit) {
    throw new Error("Tab onEdit is required when editable is true");
  }
  if (deletable && !onDelete) {
    throw new Error("Tab onDelete is required when deletable is true");
  }

  const setEdit = (status) => {
    setEditStatus(status);
    if (notifyEdit) notifyEdit(status);
  };

  const handleChange = (e) => {
    setEdit(false);

    if (originData?.[index]?.title) {
      const newData = structuredClone(originData);
      newData[index].title = editFormatter ? editFormatter(newTitle) : newTitle;
      onEdit(newData, index, newTitle);
    } else onEdit(index, newTitle);
  };

  const handleDelete = (e) => {
    // The × is inside the label of its tab: its click would also click the
    // radio of the tab (the default action of a label) and open the tab
    // first
    e?.preventDefault?.();
    e?.stopPropagation?.();

    const doDelete = () => {
      if (originData) {
        const newData = structuredClone(originData);
        newData.splice(index, 1);
        onDelete(newData, index);
      } else onDelete(index);
    };

    if (onConfirmDelete)
      onConfirmDelete().then((proceed) => {
        if (proceed) doDelete();
      });
    else doDelete();
  };

  useEffect(() => {
    if (!isActive) setShowMenu(false);
  }, [isActive]);

  useEffect(() => {
    if (showMenu) adjustMenu(menuBoxRef.current);
  }, [showMenu]);

  useLayoutEffect(() => {
    if (showMenu) {
      const element = tabBarInnerRef.current;
      const handler = () => adjustMenu(menuBoxRef.current);
      element.addEventListener("scroll", handler);
      return () => {
        element.removeEventListener("scroll", handler);
      };
    }
  }, [showMenu, tabBarInnerRef]);

  const elm = tabMenuBoxRef.current?.getBoundingClientRect() ?? {
    top: 0,
    left: 0
  };

  return (
    <>
      {edit ? (
        <TabEdit>
          <Input
            type="text"
            value={newTitle}
            className="mr-4"
            onChange={(e) => {
              setNewTitle(e.target.value);
            }}
          />
          <Button onClick={handleChange}>OK</Button>
        </TabEdit>
      ) : (
        <TabLabel
          onDoubleClick={(e) => {
            if (editable && !globalEdit) setEdit(true);
          }}
        >
          {icon && <TabIcon $color={iconColor}>{icon}</TabIcon>}
          <TabTitle
            $isActive={edit ? false : isActive}
            $color={titleColor}
            $activeColor={activeColor}
            $width={width}
          >
            {title}
          </TabTitle>
          {menu?.items.length > 0 && (
            <TabMenuBox ref={tabMenuBoxRef} $isActive={isActive}>
              {showMenu ? (
                <ChevronUp onClick={() => setShowMenu(false)} />
              ) : (
                <ChevronDown onClick={() => setShowMenu(true)} />
              )}
              {showMenu && isVisible && (
                <>
                  <Portal root={document.documentElement}>
                    <div
                      style={{
                        position: "absolute",
                        width: "24px",
                        height: "24px",
                        zIndex: 1000,
                        top: elm.y + elm.height + 2, // 2px border
                        left: elm.x
                      }}
                    >
                      <div style={{ position: "relative" }}>
                        <ArrowUp $top={0} $left={2} />
                        <HiddenDropdown $top={10} $left={-30} ref={menuBoxRef}>
                          {menu.items.map((item, index) => {
                            return (
                              <HiddenItem
                                key={`menu-item-${index}`}
                                onClick={item.onClick}
                              >
                                {item.icon && (
                                  <TabIcon $color={item.iconColor}>
                                    {item.icon}
                                  </TabIcon>
                                )}
                                <div>{item.title}</div>
                                {item.shortcut && (
                                  <>
                                    <Spacer />
                                    <HiddenItemShortcut>
                                      {item.shortcut}
                                    </HiddenItemShortcut>
                                  </>
                                )}
                              </HiddenItem>
                            );
                          })}
                        </HiddenDropdown>
                      </div>
                    </div>
                  </Portal>
                </>
              )}
            </TabMenuBox>
          )}
          {extra && <TabExtra>{extra}</TabExtra>}
          {deletable && (allowEmpty ? true : (originData?.length ?? 2) > 1) && (
            <TabDeleteIcon onClick={handleDelete}>&times;</TabDeleteIcon>
          )}
        </TabLabel>
      )}
    </>
  );
}

export default PillsContainer;
